import React, { useState, useEffect } from "react";
import { Box, IconButton, Typography, Button } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import "./ProductListing.css";
import { ProductListingProps } from "../../types/ProductList";
import editIcon from "../../assets/productCard/edit-icon1.svg";
import EyeComponent from "../eyeComponent/EyeComponent";
import { useRole } from "../../context/RoleContext";
import Toast from "../../common/Toast";
import CustomDropdown from "../../common/CustomDropdown";
import Info from "../../assets/productCard/info.svg";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useAppDispatch, useAppSelector } from "../../../src/redux/hooks";
import { addItem } from "../../redux/features/slices/cartSlice";
import { cartDataMapper } from "../../utils/cartDataMapper";
import { useCart, useUpdateCart } from "../../queries/cart";
import {
  addToWishlist,
  removeFromWishlist,
} from "../../redux/features/slices/wishlistSlice";
import { useWishlist, useToggleWishlist } from "../../queries/wishlist";
import { OrderDetail } from "../../service/cartService";
import { useProducts } from "../../queries/product";
import { notifyWhenInStockAPI } from "../../api/notificationAPI";
import { useQueryClient } from "@tanstack/react-query";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import CancelIcon from "@mui/icons-material/Cancel";

const VARIANTS = ["20L", "30L", "50L"] as const;
type Variant = (typeof VARIANTS)[number];

const getVariantQty = (product: ProductListingProps, v: Variant): number => {
  if (v === "20L") return product.availableQuantity20L ?? 0;
  if (v === "30L") return product.availableQuantity30L ?? 0;
  return product.availableQuantity50L ?? 0;
};

const isVariantOOS = (product: ProductListingProps, v: Variant) =>
  getVariantQty(product, v) === 0;

const getFirstAvailableVariant = (product: ProductListingProps): Variant => {
  for (const variant of VARIANTS) {
    if (!isVariantOOS(product, variant)) {
      return variant;
    }
  }
  return "20L";
};

const stockTag = (product: ProductListingProps, selectedVariant: Variant) => {
  if (isVariantOOS(product, selectedVariant)) {
    return {
      type: "out",
      label: "Out of stock",
      color: "#B91C1C",
      bg: "#FEF2F2",
      borderColor: "#FECACA",
    };
  }
  const oosCount = VARIANTS.filter((v) => isVariantOOS(product, v)).length;
  if (oosCount === 0)
    return {
      type: "all",
      label: "All variants in stock",
      color: "#15803D",
      bg: "#F0FDF4",
      borderColor: "#BBF7D0",
    };
  const available = 3 - oosCount;
  return {
    type: "partial",
    label: `${available} of 3 variants available`,
    color: "#A16207",
    bg: "#FEFCE8",
    borderColor: "#FEF08A",
  };
};

interface SellerCardProps {
  item: ProductListingProps;
  onEdit: (id: string) => void;
}

const SellerCard: React.FC<SellerCardProps> = ({ item, onEdit }) => {
  const [selectedVariant, setSelectedVariant] = useState<Variant>(
    getFirstAvailableVariant(item)
  );
  const [isHidden, setIsHidden] = useState(item.disabled ?? false);

  const variantOOS = isVariantOOS(item, selectedVariant);
  const cardGreyed = isHidden || variantOOS;
  const tag = isHidden
    ? {
        type: "hidden",
        label: "Hidden from customers",
        color: "#4B5563",
        bg: "#F3F4F6",
        borderColor: "#D1D5DB",
      }
    : stockTag(item, selectedVariant);
  const sellerTagLabel =
    !isHidden && tag.type === "out"
      ? "Out of stock - Restock needed"
      : tag.label;

  return (
    <div className={`pc-card ${cardGreyed ? "pc-card--greyed" : ""}`}>
      <div
        className="pc-tag"
        style={{
          color: tag.color,
          background: tag.bg,
          border: `1px solid ${tag.borderColor ?? "transparent"}`,
        }}
      >
        {isHidden ? (
          <VisibilityOffOutlinedIcon style={{ fontSize: 13, marginRight: 4 }} />
        ) : tag.type === "partial" ? (
          <WarningIcon
            style={{ fontSize: 12, marginRight: 4, color: tag.color }}
          />
        ) : tag.type === "out" ? (
          <CancelIcon
            style={{ fontSize: 12, marginRight: 1, color: "#B91C1C" }}
          />
        ) : (
          <span className="pc-tag-dot" style={{ background: tag.color }} />
        )}
        {sellerTagLabel}
      </div>

      <div className="pc-img-wrap">
        <img
          src={item.images}
          alt={item.name}
          className={`pc-img ${variantOOS || isHidden ? "pc-img--grey" : ""}`}
        />
      </div>

      <div className="pc-info">
        <Typography className="pc-name">{item.name}</Typography>
        <Typography className="pc-abv">
          {item.abv && `(${item.abv}% ABV) IBU - ${item.ibu}`}
        </Typography>
        <div className="pc-spacer" />

        <Typography className="pc-variant-label">Select Variant</Typography>
        <div className="pc-variants">
          {VARIANTS.map((v) => {
            const oos = isVariantOOS(item, v);
            return (
              <button
                key={v}
                onClick={() => setSelectedVariant(v)}
                className={`pc-variant-btn ${selectedVariant === v ? "pc-variant-btn--active" : ""} ${oos ? "pc-variant-btn--oos" : ""}`}
              >
                {v}
                {oos && <span className="pc-oos-dot" />}
              </button>
            );
          })}
        </div>

        <div className="pc-bottom">
          <Typography className="pc-price">
            ₹ {new Intl.NumberFormat("en-IN").format(Number(item.price))}/L
          </Typography>
          <div className="pc-actions">
            <div
              onClick={() => setIsHidden((h) => !h)}
              style={{ display: "contents" }}
            >
              <EyeComponent productID={item.id} disabled={isHidden} />
            </div>
            <IconButton size="small" onClick={() => onEdit(item.id)}>
              <img src={editIcon} alt="edit" className="pc-edit-icon" />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

//Customer Card
interface CustomerCardProps {
  item: ProductListingProps;
  selectedVariant: Variant;
  quantity: number;
  onVariantChange: (v: Variant) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  onAddToCart: () => void;
  onNotifyMe: () => void;
  isLiked: boolean;
  onLikeToggle: () => void;
  onImageClick: () => void;
}

const CustomerCard: React.FC<CustomerCardProps> = ({
  item,
  selectedVariant,
  quantity,
  onVariantChange,
  onIncrement,
  onDecrement,
  onAddToCart,
  onNotifyMe,
  isLiked,
  onLikeToggle,
  onImageClick,
}) => {
  const variantOOS = isVariantOOS(item, selectedVariant);
  const tag = stockTag(item, selectedVariant);

  return (
    <div className={`pc-card ${variantOOS ? "pc-card--greyed" : ""}`}>
      <button
        className={`pc-heart ${isLiked ? "pc-heart--liked" : ""}`}
        onClick={onLikeToggle}
        aria-label="Add to wishlist"
      >
        {isLiked ? (
          <FavoriteIcon style={{ fontSize: 20, color: "#E53935" }} />
        ) : (
          <FavoriteBorderOutlinedIcon style={{ fontSize: 20, color: "#555" }} />
        )}
      </button>

      <div
        className="pc-tag"
        style={{
          color: tag.color,
          background: tag.bg,
          border: `1px solid ${tag.borderColor ?? "transparent"}`,
        }}
      >
        {tag.type === "partial" ? (
          <WarningIcon
            style={{ fontSize: 12, marginRight: 4, color: tag.color }}
          />
        ) : tag.type === "out" ? (
          <CancelIcon
            style={{ fontSize: 12, marginRight: 1, color: "#B91C1C" }}
          />
        ) : (
          <span className="pc-tag-dot" style={{ background: tag.color }} />
        )}
        {tag.label}
      </div>

      <div
        className="pc-img-wrap"
        onClick={onImageClick}
        style={{ cursor: "pointer" }}
      >
        <img
          src={item.images}
          alt={item.name}
          className={`pc-img ${variantOOS ? "pc-img--grey" : ""}`}
        />
      </div>

      <div className="pc-info">
        <Typography className="pc-name">{item.name}</Typography>
        <Typography className="pc-abv">
          {item.abv && `(${item.abv}% ABV) IBU - ${item.ibu}`}
        </Typography>
        <div className="pc-spacer" />

        <Typography className="pc-price pc-price--customer">
          ₹ {new Intl.NumberFormat("en-IN").format(Number(item.price))}/L
        </Typography>

        <Typography className="pc-variant-label">Select Variant</Typography>
        <div className="pc-variants">
          {VARIANTS.map((v) => {
            const oos = isVariantOOS(item, v);
            return (
              <button
                key={v}
                onClick={() => onVariantChange(v)}
                className={`pc-variant-btn ${selectedVariant === v ? "pc-variant-btn--active" : ""} ${oos ? "pc-variant-btn--oos" : ""}`}
              >
                {v}
                {oos && <span className="pc-oos-dot" />}
              </button>
            );
          })}
        </div>

        {/* Qty + Add to Cart or Notify Me */}
        <div className="pc-customer-bottom">
          {variantOOS ? (
            <Button
              variant="outlined"
              className="pc-notify-btn"
              onClick={onNotifyMe}
            >
              Notify Me
            </Button>
          ) : (
            <div className="pc-cart-row">
              <div className="pc-qty">
                <IconButton
                  size="small"
                  onClick={onDecrement}
                  disabled={quantity <= 1}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <span className="pc-qty-count">{quantity}</span>
                <IconButton size="small" onClick={onIncrement}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </div>
              <Button
                variant="contained"
                className="pc-add-btn"
                onClick={onAddToCart}
              >
                Add to Cart
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main ProductListing
export const ProductListing = () => {
  const { role } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: products, isLoading: isProductsLoading } = useProducts();
  const [sortOrder, setSortOrder] = useState<string>("latest");
  const [sortedProducts, setSortedProducts] = useState<ProductListingProps[]>(
    []
  );
  const dispatch = useAppDispatch();

  const [selectedVariants, setSelectedVariants] = useState<{
    [productId: string]: Variant;
  }>({});

  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});

  const orgId = localStorage.getItem("orgId");
  const [error, setError] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const customerId = localStorage.getItem("customerId") || "";

  const { data: cartData } = useCart();
  const updateCart = useUpdateCart();
  const queryClient = useQueryClient();

  const cartItems = useAppSelector((state) => state.cart.items);
  const { data: wishlistQueryData } = useWishlist(customerId);
  const toggleWishlistMutate = useToggleWishlist(customerId);
  const isSellerRoute = location.pathname.includes("/maltora-seller/products");

  useEffect(() => {
    if (!cartData?.Products) return;
    const quantitiesMap: { [key: string]: number } = {};
    cartData.Products.forEach((product: OrderDetail) => {
      if (product?.productId && product?.orderQuantity) {
        const key = `${product.productId}__${product.orderQuantity}L`;
        quantitiesMap[key] = +product.unit || 1;
      }
    });
    setQuantities(quantitiesMap);
  }, [cartData]);

  useEffect(() => {
    if (!products) return;
    const sorted = [...products].sort((a, b) => {
      if (sortOrder === "latest")
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      if (sortOrder === "oldest")
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      return 0;
    });
    setSortedProducts(sorted);
  }, [sortOrder, products]);

  useEffect(() => {
    const p = wishlistQueryData?.products;
    if (Array.isArray(p)) {
      dispatch({ type: "wishlist/setWishlist", payload: p });
    }
  }, [wishlistQueryData, dispatch]);

  const wishlist = useAppSelector((state) => state.wishlist.items);

  const qtyKey = (productId: string, variant: Variant) =>
    `${productId}__${variant}`;

  const getSelectedVariant = (productId: string): Variant => {
    if (selectedVariants[productId]) {
      return selectedVariants[productId];
    }
    const product = sortedProducts.find((p) => p.id === productId);
    return product ? getFirstAvailableVariant(product) : "20L";
  };

  const getQuantity = (productId: string, variant: Variant): number =>
    quantities[qtyKey(productId, variant)] ?? 1;

  const handleVariantChange = (productId: string, variant: Variant) => {
    setSelectedVariants((prev) => ({ ...prev, [productId]: variant }));
  };

  const handleIncrement = (productId: string, variant: Variant) => {
    const key = qtyKey(productId, variant);
    setQuantities((prev) => ({ ...prev, [key]: (prev[key] ?? 1) + 1 }));
  };

  const handleDecrement = (productId: string, variant: Variant) => {
    const key = qtyKey(productId, variant);
    setQuantities((prev) => ({
      ...prev,
      [key]: Math.max((prev[key] ?? 1) - 1, 1),
    }));
  };

  const handleAddToCart = async (
    product: ProductListingProps,
    variant: Variant
  ) => {
    const selectedVolume = parseInt(variant);
    const selectedCount = getQuantity(product.id, variant);
    const availableQuantity = getVariantQty(product, variant);

    try {
      const existingItem = cartItems?.find(
        (item) =>
          String(item.id) === String(product.id) &&
          Number(item.quantity) === Number(selectedVolume)
      );
      const updatedUnit = existingItem
        ? existingItem.unit + selectedCount
        : selectedCount;

      const updatedCart = await updateCart.mutateAsync({
        cartData: [
          {
            productId: product.id,
            unit: updatedUnit,
            quantity: selectedVolume,
          },
        ],
      });

      const storeData = cartDataMapper(updatedCart?.Products || []);
      dispatch(addItem(storeData));

      const key = qtyKey(product.id, variant);
      setQuantities((prev) => ({ ...prev, [key]: 1 }));

      setToastMessage("Product added to Cart successfully");
      setError(false);
      setShowToast(true);
    } catch (err: any) {
      console.log("AddToCart error:", err, "response:", err?.response);
      const apiError =
        err?.response?.data?.error ||
        err?.cause?.response?.data?.error ||
        err?.data?.error;
      const backendMessage =
        (apiError &&
          typeof apiError.type === "object" &&
          apiError.type?.message) ||
        (apiError && typeof apiError.type === "string" && apiError.type) ||
        apiError?.message ||
        err?.response?.data?.message ||
        (err?.message && err.message !== "Failed to fetch customers"
          ? err.message
          : "");
      setToastMessage(
        backendMessage || "Something went wrong while updating the cart"
      );
      setError(true);
      setShowToast(true);
    }
  };

  const handleLikeToggle = async (product: ProductListingProps) => {
    const isLiked = wishlist.some(
      (item: ProductListingProps) => item.id === product.id
    );
    try {
      toggleWishlistMutate.mutate(product.id);
      if (isLiked) dispatch(removeFromWishlist(product.id));
      else dispatch(addToWishlist(product));
    } catch (err) {
      console.error("Failed to update wishlist", err);
    }
  };

  const handleNotifyMe = async (product: ProductListingProps) => {
    const customerEmail = localStorage.getItem("userEmail") || "";
    try {
      const response = await notifyWhenInStockAPI({
        productId: product.id,
        productName: product.name,
        customerEmail,
      });
      setToastMessage(response.message);
      setError(!response.success);
      setShowToast(true);
    } catch {
      setToastMessage("Something went wrong. Please try again later.");
      setError(true);
      setShowToast(true);
    }
  };

  const handleImageClick = (id: string) => navigate(`product/${id}`);
  const handleEdit = (id: string) => navigate(`/${role}/edit-product/${id}`);
  const handleAddProduct = () => navigate(`/${role}/create-new-product`);

  const sortOptions = [
    { value: "oldest", label: "Oldest", icon: "oldest-icon.svg" },
    { value: "latest", label: "Latest", icon: "latest-icon.svg" },
  ];

  return (
    <div>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      <Box className="filter-container">
        {isSellerRoute ? (
          <>
            <Typography variant="body1" className="title">
              Product Catalog
            </Typography>
            <Box
              className="filter-actions"
              sx={{ justifyContent: "space-between" }}
            >
              <Box className="filter-left">
                <Typography variant="body1" className="sortBy">
                  Sort By:
                </Typography>
                <Box sx={{ width: "166px", height: "41px" }}>
                  <CustomDropdown
                    label="Latest"
                    value={sortOrder}
                    options={sortOptions}
                    onChange={setSortOrder}
                  />
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={handleAddProduct}
                className="add-more-product"
              >
                <AddIcon />
                Add Products
              </Button>
            </Box>
          </>
        ) : (
          <Box className="customer-header-row">
            <Typography variant="body1" className="title">
              Our Products
            </Typography>
            <Box className="filter-actions">
              <Typography variant="body1" className="sortBy">
                Sort By:
              </Typography>
              <Box sx={{ width: "166px", height: "41px" }}>
                <CustomDropdown
                  label="Latest"
                  value={sortOrder}
                  options={sortOptions}
                  onChange={setSortOrder}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {!isSellerRoute && (
        <Box className="notice-message">
          <img src={Info} alt="info" />
          <Typography variant="body2">
            Please Note:{" "}
            <span className="note">
              Prices shown are per litre. Final invoice amount will be
              calculated based on the selected volume.
            </span>
          </Typography>
        </Box>
      )}

      <Box className="products-wrapper">
        {!isProductsLoading && sortedProducts.length === 0 && (
          <Box
            sx={{
              width: "100%",
              py: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: "#6b7280",
                fontFamily: "Roboto",
              }}
            >
              No products added yet.
            </Typography>
            {isSellerRoute && (
              <Typography
                sx={{ fontSize: 13, color: "#9ca3af", fontFamily: "Roboto" }}
              >
                Click &quot;Add Products&quot; to add your first product to the
                catalog.
              </Typography>
            )}
          </Box>
        )}
        {sortedProducts.map((item) => {
          if (isSellerRoute) {
            return <SellerCard key={item.id} item={item} onEdit={handleEdit} />;
          }

          const variant = getSelectedVariant(item.id);
          const qty = getQuantity(item.id, variant);

          return (
            <CustomerCard
              key={item.id}
              item={item}
              selectedVariant={variant}
              quantity={qty}
              onVariantChange={(v) => handleVariantChange(item.id, v)}
              onIncrement={() => handleIncrement(item.id, variant)}
              onDecrement={() => handleDecrement(item.id, variant)}
              onAddToCart={() => handleAddToCart(item, variant)}
              onNotifyMe={() => handleNotifyMe(item)}
              isLiked={wishlist.some(
                (w: ProductListingProps) => w.id === item.id
              )}
              onLikeToggle={() => handleLikeToggle(item)}
              onImageClick={() => handleImageClick(item.id)}
            />
          );
        })}
      </Box>
    </div>
  );
};

export default ProductListing;
